import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import * as _ from 'lodash';
import { ConfigConstants } from '../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../interfaces/client-manager.interface';
import { CodeModel } from '../models/complex/code.model';
import { NotificationIdentifierModel } from '../models/low-level/notification-identifier.model';
import { NotificationModel } from '../models/low-level/notification.model';
import { TreeViewModelOptions } from '../models/options/treeview-model-options';
import { TreeViewNode } from '../models/primitive/tree-view-node';
import { TreeViewModel } from '../models/primitive/tree-view.model';
import { ConfigService } from '../services/config/config.service';
import { ValueHelperService } from '../services/value-helper/value-helper.service';
import { UserMasterService } from '../services/user-master/user-master.service';

@Injectable({
    providedIn: 'root',
})
export class NotificationUtilities {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly valueHelperService: ValueHelperService,
        private readonly configService: ConfigService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly userMasterService: UserMasterService,
    ) {}

    /**
     * To determines whether the pace forms feature should be enabled for a notification based on
     * 1. Configuration from config set at gateway
     * 2. Notification's form count
     * 3. Notification's type
     *
     * @param formsCount
     */
    public isPaceFormsAvailable(formsCount: number): boolean {
        return this.clientManager.isNgmP58() && !this.configService.getDisablePaceForms() && formsCount > 0;
    }

    public getNotificationTypeDescription(notification: NotificationModel): string {
        const type = this.valueHelperService.getNotificationTypeLabel(notification.notificationType);
        return type + ' ' + notification.identifier.getQmnum() + '  (' + notification.identifier.getManum() + ')';
    }

    /**
     * To get the description of the in arrival status on the basis of the Notification's effect code
     *
     * @param notification Notification(might be from NDP) with effect code
     * @returns Description of the in arrival status on the basis of effect code
     */
    public getNotificationStatusInArrivalDescription(notification: NotificationModel): string {
        const equipmentAllStatusInArrival = notification.isCentriCareContract()
            ? this.valueHelperService.getCentriCareStatInArrival()
            : this.valueHelperService.getEquipmentStatusInArrival();
        const notificationStausInArrival = equipmentAllStatusInArrival.find(
            (status) => status.code === notification.effect.toString(),
        );
        return notificationStausInArrival ? notificationStausInArrival.descText : '';
    }

    public getEquipmentErrorCodes(notification: NotificationModel): TreeViewModel<CodeModel> | undefined {
        // TreeViewNodes get an incremental id so they can be uniquely identified regardless of their actual content
        let nodeId = 1; // we have to start at 1, otherwise the ng2-tree component will not accept the id

        // filter for matching CodeModels and create TreeViewNodes around them
        let nodes = notification.codeSet
            .filter((code) => code.codeType.value === '5')
            .map((code) => new TreeViewNode<CodeModel>(nodeId++, code.codeDesc.value, code));

        // Find root node (QKATART = '0') and build tree up from there
        const rootNode = _.remove(nodes, (node) => node.payload.qkatart.value === '0').shift();
        if (typeof rootNode === 'undefined') {
            this.log.debug(
                `getEquipmentErrorCodes: Could not find root node for reason codes of notification ${notification.identifier.toString()}. Number of codes of type == '5': ${
                    nodes.length
                }.`,
            );
            return undefined;
        }

        // Sort nodes array according to hierarchy depth = number of dots in 'qkatart'
        nodes = _.sortBy(nodes, (node) => (node.payload.qkatart.value.match(RegExp('.', 'g')) || []).length);

        // Create hierarchical structure by adding the nodes one by one to their respective parent node.
        // Due to the sorting in the previous step the hierarchical levels should be filled top to bottom without
        // missing nodes in between.
        nodes.forEach((node) => {
            // Start the descent at the root node
            let parent = rootNode;

            // Create an array of the hierarchy relation given in 'qkatart'. Example: "1.3.2" => [ 1, 3, 2 ]
            const hierarchy = node.payload.qkatart.value.split('.').map((index) => {
                if (index === '00') {
                    return 100; // special cases for nodes having qkatart = "2.18.00" which is actually representing "2.18.100"
                }
                return parseInt(index, 10);
            });

            // The index in the hierarchy array at which the index of this node in regard to its parent can be found (=last entry)
            const depth = hierarchy.length - 1;

            // Descend through tree to find correct parent node
            for (let i = 0; i < depth; i++) {
                // Hierarchy numbering starts at 1, array indices at 0 -> subtract 1 from hierarchy level
                parent = parent.children[hierarchy[i] - 1];
                if (!parent) {
                    throw new Error(
                        `getEquipmentErrorCodes: Malformed 'qkatart' for ${notification.identifier.toString()}:
                         ${node.payload.qkatart.value} has no suitable parent at depth ${i}!`,
                    );
                }
            }

            // Hierarchy numbering starts at 1, array indices at 0 -> subtract 1
            const insertIndex = hierarchy[depth] - 1;

            /*
             * The next part is special because Siemens is special. Leaf nodes do not have a hierarchy level of their
             * own (because that would be the sane approach). No, instead the parent of leaf nodes and the leaf nodes
             * themselves are on the same hierarchy level. To know which of these nodes is supposed to be the parent and
             * which nodes are true leaf nodes, you have to look at the codeGroup. The parent will have a value of
             * "XXXXX" as codeGroup while the leaf nodes will have some other value.
             *
             * Example:
             *
             * QKATART      CODEGROUP    CODE    CODLT
             * -------------------------------------------------
             * 2.3          XXXXX                PATIENT TABLE
             * 2.3.20       XXXXX                VERTICAL DRIVE   -> Parent node 2.3.20
             * 2.3.20       HW03230      2000    USER RELAT       -> Leaf node under 2.3.20
             * 2.3.20       HW03230      2001    ADJUSTMENT       -> Leaf node under 2.3.20
             * 2.3.20       HW03230      2002    DEFECT           -> Leaf node under 2.3.20
             *
             * (taken out out ticket https://evorait.atlassian.net/browse/SHCNGMP58S-15)
             */

            // check if the node is a leaf node or a parent node based on the codeGroup
            if (node.payload.codeGroup.value === 'XXXXX') {
                // this is a parent node
                if (parent.children[insertIndex]) {
                    this.log.warn('Overwriting node: ', parent.value, parent.children[insertIndex].value, node.value);
                }

                // Insert node at correct position.
                parent.children[insertIndex] = node;

                // create relation to parent
                node.parent = parent;
            } else {
                // this is a leaf node
                // the parent of this leaf node should already exist
                if (!parent.children[insertIndex]) {
                    this.log.warn(
                        `Parent node for leaf does not exist yet on index ${insertIndex}: `,
                        parent.value,
                        node.value,
                    );
                }

                // fix parent node
                parent = parent.children[insertIndex];

                // insert leaf node into parent
                parent.children.push(node);

                // create relation of leaf node to parent node
                node.parent = parent;
            }
        });

        // Make sure there are no holes in the tree structure (=entries in 'nodes' that still point to 'undefined')
        let nodesToCheck = <Array<TreeViewNode<CodeModel>>>[];
        nodesToCheck.push(rootNode);
        while (nodesToCheck.length > 0) {
            const currentNode = nodesToCheck.shift();
            if (!currentNode) {
                continue;
            }

            // compact nodes array by removing 'undefined' entries
            currentNode.children = currentNode.children.filter((node) => typeof node !== 'undefined');

            // this.log.debug(`getEquipmentErrorCodes: Node: '${currentNode.title}' with ${currentNode.children.length} children (was: ${initialLength})`);

            nodesToCheck = nodesToCheck.concat(currentNode.children);
            // this.log.debug(`getEquipmentErrorCodes: Added ${currentNode.children.length} nodes to check (now remaining: ${nodesToCheck.length})`);
        }

        // this.log.debug(`Constructing the treeview model took ${moment().diff(startMoment, "milliseconds")} ms.`);

        // TODO: Get number of possible selections from gateway/config service
        return new TreeViewModel<CodeModel>(
            'CauseCodeTree',
            rootNode,
            new TreeViewModelOptions({
                maxSelections: 1,
                required: true,
            }),
        );
    }

    public static getNotificationsFormattedWithNewLine(
        notifications: NotificationModel[],
        translateString: string,
    ): string {
        const notificationsToBeFormatted = notifications.reduce(
            (accu, notification) =>
                accu.length > 0
                    ? `${accu}, ${notification.identifier.getQmnum()}:${notification.identifier.getManum()} `
                    : `${notification.identifier.getQmnum()}:${notification.identifier.getManum()}`,
            '',
        );
        return translateString.concat(notificationsToBeFormatted.replace(/,/g, '<br>'));
    }

    public static getUniqueIdentifier(id: NotificationIdentifierModel, separator = '-'): string {
        return `${id.frontId}${separator}${id.qmNum}${separator}${id.maNum}`;
    }

    public getNotificationCategory(notification: NotificationModel): string[] {
        let categoryList: string[] = [];
        categoryList = notification.codeSet
            .filter(
                (code) =>
                    code.codeType.value === ConfigConstants.CodeTypeForActivity &&
                    code.codeGroup.value.length > 0 &&
                    !categoryList.includes(code.codeGroup.value),
            )
            .map((code) => code.codeGroup.value);

        return categoryList;
    }

    public isInstallationNotificationWithIHOT(notification: NotificationModel): boolean {
        return (
            notification.notificationType === ConfigConstants.NotificationTypes.Installation &&
            notification.codeSet.some(
                (code) =>
                    code.codeType.value === ConfigConstants.CodeTypeForActivity &&
                    code.codeGroup.value === ConfigConstants.CodeGroupForInitialHandoverTraining,
            )
        );
    }
}
